import { useEffect, useMemo, useRef } from "react";
import { isWeb, type TamaguiElement, VisuallyHidden } from "tamagui";
import { Chip } from "./Chip";
import { XStack, YStack } from "./Stacks";
import { Interface3 } from "./Typography";
export type ChipGroupItem = {
  value: string;
  label: string;
};
export type ChipGroupProps = {
  items: ChipGroupItem[];
  onRemove: (value: string) => void;
  /** Names the chip group for screen readers (e.g. the field label). */
  groupLabel: string;
  disabled?: boolean;
  /** Focus target for the removal that empties the group — typically the
   *  field's trigger, so a keyboard user isn't dropped back to the body. */
  focusAfterLastRemove?: () => void;
};

/**
 * A group of removable `Chip`s with a persistent visually-hidden `aria-live`
 * region announcing the count (APG doesn't announce it for a custom group).
 * Rendered even when empty so the live region stays mounted across the 0↔1
 * transition. The `ChipGroup` → `Chip` → `StyledPill` layering mirrors
 * `SelectionGroup` → `SelectionRow`/`Tile` → `SelectionCard`. Used as the
 * chips-below value display of multi `Select` / `Combobox`, but usable for any
 * removable-chip list.
 */
export const ChipGroup = ({
  items,
  onRemove,
  groupLabel,
  disabled = false,
  focusAfterLastRemove
}: ChipGroupProps) => {
  const groupRef = useRef<TamaguiElement>(null);
  const removedIndexRef = useRef<number | null>(null);

  // Removing a chip unmounts the focused element and the browser drops focus
  // to the body, so a keyboard user would have to tab back in from the top of
  // the page between removals. Restore it: the chip now at the removed index
  // (clamped to the last), or the field when the group emptied.
  useEffect(() => {
    const removedIndex = removedIndexRef.current;
    if (!isWeb || removedIndex === null) {
      return;
    }
    removedIndexRef.current = null;
    if (items.length === 0) {
      focusAfterLastRemove?.();
      return;
    }
    const group = groupRef.current;
    if (group instanceof HTMLElement) {
      const chips = group.querySelectorAll("button");
      chips[Math.min(removedIndex, chips.length - 1)]?.focus();
    }
  }, [items, focusAfterLastRemove]);

  // Stable handler per chip so `memo(Chip)` holds across a parent's unrelated
  // re-renders (e.g. Combobox keystrokes); rebuilt only when the set changes.
  const removeHandlers = useMemo(() => items.map((item, index) => () => {
    removedIndexRef.current = index;
    onRemove(item.value);
  }), [items, onRemove]);
  return <YStack gap="$sp1" paddingTop={items.length > 0 ? "$sp1" : undefined} data-sentry-element="YStack" data-sentry-component="ChipGroup" data-sentry-source-file="ChipGroup.tsx">
      <VisuallyHidden data-sentry-element="VisuallyHidden" data-sentry-source-file="ChipGroup.tsx">
        <Interface3 aria-live="polite" data-sentry-element="Interface3" data-sentry-source-file="ChipGroup.tsx">{`${items.length} selected`}</Interface3>
      </VisuallyHidden>
      {items.length > 0 ? <XStack ref={groupRef} flexWrap="wrap" gap="$sp1" role="group" aria-label={groupLabel}>
          {items.map((item, index) => <Chip key={item.value} label={item.label} onRemove={removeHandlers[index]} disabled={disabled} />)}
        </XStack> : null}
    </YStack>;
};